'use client';

import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { useTranslations } from 'next-intl';
import { Icon, type IconName } from './icon';
import styles from './toast.module.css';

type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info';

/** A single recovery affordance on a toast — "Undo", "Retry", "View". */
export interface ToastActionButton {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: string;
  title?: string;
  message: string;
  variant: ToastVariant;
  action?: ToastActionButton;
}

type ToastAction =
  | { type: 'ADD'; toast: ToastItem }
  | { type: 'UPDATE'; id: string; patch: Partial<ToastItem> }
  | { type: 'REMOVE'; id: string };

function toastReducer(state: ToastItem[], action: ToastAction): ToastItem[] {
  switch (action.type) {
    case 'ADD':
      return [...state, action.toast];
    case 'UPDATE':
      return state.map((t) => (t.id === action.id ? { ...t, ...action.patch } : t));
    case 'REMOVE':
      return state.filter((t) => t.id !== action.id);
    default:
      return state;
  }
}

const TOAST_ICON: Record<ToastVariant, IconName | null> = {
  default: 'info',
  success: 'success',
  error: 'error',
  warning: 'warn',
  info: 'info',
};

const VARIANT_CLASS: Record<ToastVariant, string | null> = {
  default: null,
  success: 'success',
  error: 'error',
  warning: 'warning',
  info: 'info',
};

interface ToastOptions {
  title?: string;
  variant?: ToastVariant;
  /**
   * Recovery affordance rendered inside the toast. Use where the action being
   * confirmed is otherwise unrecoverable without navigating elsewhere — e.g.
   * undoing a mis-tapped "always in stock" on the shopping list.
   */
  action?: ToastActionButton;
  /**
   * Override auto-dismiss timeout (ms). Errors default to no auto-dismiss;
   * every other variant defaults to 4000ms. Pass `null` to keep any toast up
   * until it is updated or dismissed programmatically (e.g. a progress toast).
   */
  duration?: number | null;
}

/** Compute the effective auto-dismiss delay: an explicit `duration` (incl. an
 *  explicit `null` for "persist") wins; otherwise errors persist and everything
 *  else dismisses after 4s. */
function resolveDuration(variant: ToastVariant, options?: ToastOptions): number | null {
  return options?.duration !== undefined
    ? options.duration
    : variant === 'error'
      ? null
      : 4000;
}

interface ToastContextValue {
  /** Show a toast; returns its id so it can be updated/dismissed later. */
  toast: (message: string, options?: ToastOptions) => string;
  /** Replace an existing toast's message/variant/title in place and reschedule
   *  its auto-dismiss per the new options (e.g. flip a persistent "in progress"
   *  toast to a self-dismissing "done" one). No-op if the id is gone. */
  updateToast: (id: string, message: string, options?: ToastOptions) => void;
  /** Dismiss a toast immediately. No-op if the id is gone. */
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProps {
  variant?: ToastVariant;
  title?: ReactNode;
  children?: ReactNode;
  /** Recovery affordance. Runs its handler, then dismisses the toast. */
  action?: ToastActionButton;
  onDismiss?: () => void;
  className?: string;
}

export function Toast({ variant = 'default', title, children, action, onDismiss, className }: ToastProps) {
  const tc = useTranslations('common');
  const isAlert = variant === 'error' || variant === 'warning';
  const iconName = TOAST_ICON[variant];
  const variantClass = VARIANT_CLASS[variant];
  const classes = [styles.toast, variantClass ? styles[variantClass] : null, className]
    .filter(Boolean)
    .join(' ');
  return (
    <div
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
      className={classes}
    >
      <span className={styles.icon} aria-hidden="true">
        {iconName ? <Icon name={iconName} size={18} /> : null}
      </span>
      <div className={styles.message}>
        {title ? <div className={styles.title}>{title}</div> : null}
        {children ? <div className={styles.body}>{children}</div> : null}
      </div>
      {/* action + close share the ONE trailing grid cell so an action doesn't
          become a 4th grid child squeezed into the close column. */}
      {action || onDismiss ? (
        <div className={styles.actions}>
          {action ? (
            <button
              type="button"
              className={styles.action}
              onClick={() => {
                action.onClick();
                onDismiss?.();
              }}
            >
              {action.label}
            </button>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              className={styles.close}
              onClick={onDismiss}
              aria-label={tc('dismiss')}
            >
              <Icon name="x" size={16} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const tn = useTranslations('notifications');
  const [toasts, dispatch] = useReducer(toastReducer, []);
  // Per-toast auto-dismiss timers, so a toast can be re-scheduled on update
  // (e.g. a persistent progress toast that later flips to auto-dismiss) or have
  // its pending timer cancelled on manual dismiss.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearTimer = useCallback((id: string) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const remove = useCallback((id: string) => {
    clearTimer(id);
    dispatch({ type: 'REMOVE', id });
  }, [clearTimer]);

  const schedule = useCallback((id: string, duration: number | null) => {
    clearTimer(id);
    if (duration !== null) {
      timers.current.set(id, setTimeout(() => remove(id), duration));
    }
  }, [clearTimer, remove]);

  const toast = useCallback(
    (message: string, options?: ToastOptions): string => {
      const id = crypto.randomUUID();
      const variant = options?.variant ?? 'default';
      dispatch({
        type: 'ADD',
        toast: { id, message, title: options?.title, variant, action: options?.action },
      });
      schedule(id, resolveDuration(variant, options));
      return id;
    },
    [schedule],
  );

  const updateToast = useCallback(
    (id: string, message: string, options?: ToastOptions): void => {
      const variant = options?.variant ?? 'default';
      const patch: Partial<ToastItem> = { message, variant };
      if (options?.title !== undefined) patch.title = options.title;
      dispatch({ type: 'UPDATE', id, patch });
      schedule(id, resolveDuration(variant, options));
    },
    [schedule],
  );

  const dismissToast = useCallback((id: string): void => remove(id), [remove]);

  return (
    <ToastContext.Provider value={{ toast, updateToast, dismissToast }}>
      {children}
      <div className={styles.container} aria-label={tn('heading')}>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            title={t.title}
            action={t.action}
            onDismiss={() => remove(t.id)}
          >
            {t.message}
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
